import Auth0ProviderWithHistory from 'auth/Auth0ProviderWithHistory';
import AuthHandler from 'auth/AuthHandler';
import { DotLoader } from 'components/DotLoader';
import { Layout } from 'components/Layout/Layout';
import { RouteErrorElement } from 'components/RouteErrorElement/RouteErrorElement';
import { RouteLoadingBar } from 'components/RouteLoadingBar/RouteLoadingBar';
import { PATHNAMES } from 'constants/constants';
import { ConferenceSignup } from 'pages/ConferenceSignup/ConferenceSignup';
import { NotFoundContent, NotFoundPage } from 'pages/NotFoundPage/NotFoundPage';
import type { FC } from 'react';
import { useEffect } from 'react';
import { createBrowserRouter, Outlet, useNavigation } from 'react-router';
import { useAppSelector } from 'redux/store';
import { anticipationRoutes } from 'routes/AnticipationRoutes';
import { ftoRoutes } from 'routes/FTORoutes';
import { generalAssistantRoutes } from 'routes/GeneralAssistantRoutes';
import { infringementDetectionRoutes } from 'routes/InfringementDetectionRoutes';
import { inventionAssistantRoutes } from 'routes/InventionAssistantRoutes';
import { inventionDisclosureRoutes } from 'routes/InventionDisclosureRoutes';
import { litigationAssistantRoutes } from 'routes/LitigationAssistantRoutes';
import { officeActionV2Routes } from 'routes/OfficeActionV2Routes';
import {
  patentDraftingDisclosureRoutes,
  patentDraftingPatentRoutes,
  patentDraftingResolverRoutes,
} from 'routes/PatentDraftingRoutes';
import type { RouteObjectWithLazyPreload } from 'utils/lazyRoute';
import { lazyRoute, preloadRoutes } from 'utils/lazyRoute';
import { WORKFLOWS } from 'utils/roles/types';
import { useRolesCheck } from 'utils/roles/useRolesCheck';

const AllProjects = lazyRoute(
  () => import('features/ProjectsHub'),
  m => m.AllProjects,
);
const ProjectsAnalytics = lazyRoute(
  () => import('features/ProjectsHub'),
  m => m.ProjectsAnalytics,
);
const HomeHub = lazyRoute(
  () => import('features/ProjectsHub'),
  m => m.HomeHub,
);
const PatentMoreInfo = lazyRoute(
  () => import('pages/PatentMoreInfo/PatentMoreInfo'),
  m => m.PatentMoreInfo,
);
const ProjectFolderDetailPage = lazyRoute(
  () => import('pages/ProjectFolderDetail/ProjectFolderDetailPage'),
  m => m.ProjectFolderDetailPage,
);
const ReleaseNotes = lazyRoute(
  () => import('pages/ReleaseNotes'),
  m => m.ReleaseNotes,
);
const AISearch = lazyRoute(
  () => import('pages/AISearch'),
  m => m.AISearch,
);
const AISearchV2 = lazyRoute(
  () => import('pages/AISearchV2'),
  m => m.AISearchV2,
);
const ExternalSharepointLoginPage = lazyRoute(
  () => import('pages/ExternalSharepointLoginPage/ExternalSharepointLoginPage'),
  m => m.ExternalSharepointLoginPage,
);
const PatentIntelligence = lazyRoute(
  () => import('pages/PatentIntelligence'),
  m => m.PatentIntelligence,
);
const ApiDocs = lazyRoute(
  () => import('pages/ApiDocs'),
  m => m.ApiDocs,
);
const ComponentDemo = lazyRoute(
  () => import('pages/ComponentDemo/ComponentDemo'),
  m => m.ComponentDemo,
);
const ConferenceAdminPanel = lazyRoute(
  () => import('pages/ConferenceAdminPanel/ConferenceAdminPanel'),
  m => m.ConferenceAdminPanel,
);
const DeveloperAdminPanel = lazyRoute(
  () => import('pages/DeveloperAdminPanel'),
  m => m.DeveloperAdminPanel,
);
const DeveloperPanel = lazyRoute(
  () => import('pages/DeveloperPanel'),
  m => m.DeveloperPanel,
);
const OrganizationAdminPanel = lazyRoute(
  () => import('pages/OrganizationAdminPanel'),
  m => m.OrganizationAdminPanel,
);
const OrganizationSettingsPanel = lazyRoute(
  () => import('pages/OrganizationSettingsPanel'),
  m => m.OrganizationSettingsPanel,
);
const PlatformAdminPanel = lazyRoute(
  () => import('pages/PlatformAdminPanel'),
  m => m.PlatformAdminPanel,
);
const FeaturesAdminPanel = lazyRoute(
  () => import('./pages/FeatureFlagsViewPanel'),
  m => m.FeaturesAdminPanel,
);

type CanAccessWorkflowFn = (workflow: WORKFLOWS) => boolean | null;
interface RoleGatedRouteGroup {
  isAccessible: (canAccessWorkflow: CanAccessWorkflowFn, isDeveloper: boolean | null | undefined) => boolean;
  routes: RouteObjectWithLazyPreload[];
}

const roleGatedRouteGroups: RoleGatedRouteGroup[] = [
  {
    isAccessible: () => true,
    routes: [
      { path: PATHNAMES.main, lazy: HomeHub },
      { path: PATHNAMES.allProjects, lazy: AllProjects },
      { path: PATHNAMES.analytics, lazy: ProjectsAnalytics },
      { path: PATHNAMES.projectFolder, lazy: ProjectFolderDetailPage },
      { path: `${PATHNAMES.patentMoreInfo}/:patentId`, lazy: PatentMoreInfo, skipPreload: true },
      { path: PATHNAMES.releaseNotes, lazy: ReleaseNotes, skipPreload: true },
    ],
  },
  { isAccessible: canAccess => !!canAccess(WORKFLOWS.FTO), routes: ftoRoutes },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.INFRINGEMENT_DETECTION),
    routes: infringementDetectionRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.INVENTION_DISCLOSURE),
    routes: inventionDisclosureRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.OFFICE_ACTIONS),
    routes: officeActionV2Routes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.LITIGATION_ASSISTANT),
    routes: litigationAssistantRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.GENERAL_ASSISTANT),
    routes: generalAssistantRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.INVENTION_ASSISTANT),
    routes: inventionAssistantRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.ANTICIPATION),
    routes: anticipationRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.DISCLOSURE_DRAFT),
    routes: patentDraftingDisclosureRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.PATENT_DRAFT),
    routes: patentDraftingPatentRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.DISCLOSURE_DRAFT) || !!canAccess(WORKFLOWS.PATENT_DRAFT),
    routes: patentDraftingResolverRoutes,
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.AI_SEARCH),
    routes: [{ path: `${PATHNAMES.aiSearch}/:searchSessionId?`, lazy: AISearch }],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.CONCEPT_SEARCH),
    routes: [{ path: `${PATHNAMES.aiSearchV2}/:searchId?`, lazy: AISearchV2 }],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.PATENT_INTELLIGENCE),
    routes: [{ path: PATHNAMES.patentIntelligence, lazy: PatentIntelligence }],
  },
  {
    isAccessible: (_canAccessWorkflow, isDeveloper) => !!isDeveloper,
    routes: [
      { path: PATHNAMES.apiDocs, lazy: ApiDocs, skipPreload: true },
      { path: PATHNAMES.componentsDemo, lazy: ComponentDemo, skipPreload: true },
      { path: `${PATHNAMES.developerPanel}/:section?`, lazy: DeveloperPanel, skipPreload: true },
    ],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.ORG_MANAGEMENT),
    routes: [
      { path: `${PATHNAMES.orgAdminPanel}/user?/:mode?/:userId?`, lazy: OrganizationAdminPanel, skipPreload: true },
    ],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.PLATFORM_MANAGEMENT),
    routes: [
      { path: PATHNAMES.platformAdminPanel, lazy: PlatformAdminPanel, skipPreload: true },
      {
        path: `${PATHNAMES.platformAdminPanel}/organization/create`,
        lazy: OrganizationSettingsPanel,
        skipPreload: true,
      },
      {
        path: `${PATHNAMES.platformAdminPanel}/organization/:organizationId/:section?`,
        lazy: OrganizationSettingsPanel,
        skipPreload: true,
      },
      {
        path: `${PATHNAMES.platformAdminPanel}/organization/:organizationId/:section/:mode/:userId?`,
        lazy: OrganizationSettingsPanel,
        skipPreload: true,
      },
    ],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.FEATURES_FLAGS_VIEW),
    routes: [{ path: `${PATHNAMES.featureFlagsPanel}/`, lazy: FeaturesAdminPanel, skipPreload: true }],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.DEVELOPER_MANAGEMENT),
    routes: [{ path: PATHNAMES.developerAdminPanel, lazy: DeveloperAdminPanel, skipPreload: true }],
  },
  {
    isAccessible: canAccess => !!canAccess(WORKFLOWS.CONFERENCE_MANAGEMENT),
    routes: [
      {
        path: `${PATHNAMES.conferenceAdminPanel}/:mode?/:conferenceId?`,
        lazy: ConferenceAdminPanel,
        skipPreload: true,
      },
    ],
  },
];

const WorkflowGuard: FC<{ group: RoleGatedRouteGroup }> = ({ group }) => {
  'use memo';

  const { canAccessWorkflow, isDeveloper } = useRolesCheck();

  if (!group.isAccessible(canAccessWorkflow, isDeveloper)) {
    return <NotFoundContent />;
  }

  return <Outlet />;
};

const ProtectedRoute: FC = () => {
  'use memo';

  const { user } = useAppSelector(store => store.authSlice);
  const { canAccessWorkflow, isDeveloper } = useRolesCheck();
  const navigation = useNavigation();

  useEffect(() => {
    if (!user) {
      return;
    }

    if (import.meta.env.DEV) {
      return;
    }

    for (const group of roleGatedRouteGroups) {
      if (group.isAccessible(canAccessWorkflow, isDeveloper)) {
        preloadRoutes(group.routes);
      }
    }
  }, [user, canAccessWorkflow, isDeveloper]);

  if (!user) {
    return <DotLoader fullScreen />;
  }

  return (
    <Layout>
      {navigation.state !== 'idle' && <RouteLoadingBar />}
      <Outlet />
    </Layout>
  );
};

export const router = createBrowserRouter([
  {
    element: (
      <Auth0ProviderWithHistory>
        <AuthHandler>
          <Outlet />
        </AuthHandler>
      </Auth0ProviderWithHistory>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { path: PATHNAMES.authLogin, element: null },
      { path: PATHNAMES.externalSharepointLogin, lazy: ExternalSharepointLoginPage },
      {
        Component: ProtectedRoute,
        hydrateFallbackElement: <DotLoader fullScreen />,
        children: roleGatedRouteGroups.map(group => ({
          Component: () => <WorkflowGuard group={group} />,
          children: group.routes,
        })),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: 'conference/*', Component: ConferenceSignup, errorElement: <RouteErrorElement /> },
]);
